import type { Budget, ShapeData, Vec3 } from '@/lib/cloud/types'

import { CloudBuilder } from '@/lib/cloud/builder'
import { PALETTE, mixRgb } from '@/lib/cloud/palette'
import { allocate, createRng } from '@/lib/cloud/rng'
import {
    KIND_GROUND,
    KIND_HOT,
    KIND_LIVE,
    KIND_STRUCTURE
} from '@/lib/cloud/types'

export const GLOBE_RADIUS = 10

export const latLon = (lat: number, lon: number, radius: number = GLOBE_RADIUS): Vec3 => {
    const phi = (lat * Math.PI) / 180
    const lambda = (lon * Math.PI) / 180

    return [
        radius * Math.cos(phi) * Math.sin(lambda),
        radius * Math.sin(phi),
        radius * Math.cos(phi) * Math.cos(lambda)
    ]
}

export const STOCKHOLM = { lat: 59.3293, lon: 18.0686 }
export const LISBON = { lat: 38.7223, lon: -9.1393 }

const normalize = (v: Vec3): Vec3 => {
    const length = Math.hypot(v[0], v[1], v[2]) || 1

    return [v[0] / length, v[1] / length, v[2] / length]
}

const slerp = (a: Vec3, b: Vec3, t: number): Vec3 => {
    const dot = Math.min(1, Math.max(-1, a[0] * b[0] + a[1] * b[1] + a[2] * b[2]))
    const omega = Math.acos(dot)

    if (omega < 1e-5) return a

    const sa = Math.sin((1 - t) * omega) / Math.sin(omega)
    const sb = Math.sin(t * omega) / Math.sin(omega)

    return [a[0] * sa + b[0] * sb, a[1] * sa + b[1] * sb, a[2] * sa + b[2] * sb]
}

/**
 * A globe of points with a graticule, two office markers and the arc between Stockholm and Lisbon.
 */
export const createGlobe = (budget: Budget, seed: number = 808): ShapeData => {
    const rng = createRng(seed)
    const b = new CloudBuilder(budget, rng)
    const P = PALETTE

    b.use(KIND_STRUCTURE)

    const [nSphere, nGraticule] = allocate(b.capacity(KIND_STRUCTURE), [72, 28])
    const golden = Math.PI * (3 - Math.sqrt(5))

    b.style(P.point, 0.22, 0.9).jitter(0)

    for (let i = 0; i < nSphere; i++) {
        const y = 1 - (2 * (i + 0.5)) / nSphere
        const radius = Math.sqrt(1 - y * y)
        const theta = golden * i

        b.point(Math.cos(theta) * radius * GLOBE_RADIUS, y * GLOBE_RADIUS, Math.sin(theta) * radius * GLOBE_RADIUS)
    }

    const parallels = [-60, -30, 0, 30, 60]
    const meridians = Array.from({ length: 12 }, (_, index) => index * 30)
    const circleCounts = allocate(nGraticule, [
        ...parallels.map(lat => Math.cos((lat * Math.PI) / 180)),
        ...meridians.map(() => 0.5)
    ])

    parallels.forEach((lat, index) => {
        const n = circleCounts[index]

        b.style(P.point, lat === 0 ? 0.6 : 0.42, 0.95)

        for (let k = 0; k < n; k++) {
            const point = latLon(lat, (k / n) * 360 - 180, GLOBE_RADIUS * 1.002)

            b.point(point[0], point[1], point[2])
        }
    })

    meridians.forEach((lon, index) => {
        const n = circleCounts[parallels.length + index]

        b.style(P.point, lon === 0 ? 0.6 : 0.4, 0.95)

        for (let k = 0; k < n; k++) {
            const point = latLon((k / Math.max(1, n - 1)) * 180 - 90, lon, GLOBE_RADIUS * 1.002)

            b.point(point[0], point[1], point[2])
        }
    })

    b.use(KIND_HOT).jitter(0)

    const [nStockholm, nLisbon] = allocate(b.capacity(KIND_HOT), [1, 1])

    const marker = (lat: number, lon: number, n: number): void => {
        const centre = latLon(lat, lon, GLOBE_RADIUS * 1.01)
        const normal = normalize(centre)
        const helper: Vec3 = Math.abs(normal[1]) > 0.9 ? [1, 0, 0] : [0, 1, 0]
        const u = normalize([
            helper[1] * normal[2] - helper[2] * normal[1],
            helper[2] * normal[0] - helper[0] * normal[2],
            helper[0] * normal[1] - helper[1] * normal[0]
        ])
        const v: Vec3 = [
            normal[1] * u[2] - normal[2] * u[1],
            normal[2] * u[0] - normal[0] * u[2],
            normal[0] * u[1] - normal[1] * u[0]
        ]

        for (let k = 0; k < n; k++) {
            const ring = k % 3 === 0
            const angle = rng() * Math.PI * 2
            const r = ring ? 0.75 : 0.22 * Math.sqrt(rng())
            const x = centre[0] + (u[0] * Math.cos(angle) + v[0] * Math.sin(angle)) * r
            const y = centre[1] + (u[1] * Math.cos(angle) + v[1] * Math.sin(angle)) * r
            const z = centre[2] + (u[2] * Math.cos(angle) + v[2] * Math.sin(angle)) * r

            b.paint(x, y, z, P.signal, ring ? 0.7 : 1, ring ? 0.9 : 1.6)
        }
    }

    marker(STOCKHOLM.lat, STOCKHOLM.lon, nStockholm)
    marker(LISBON.lat, LISBON.lon, nLisbon)

    b.use(KIND_LIVE).jitter(0.02)

    const from = normalize(latLon(STOCKHOLM.lat, STOCKHOLM.lon))
    const to = normalize(latLon(LISBON.lat, LISBON.lon))
    const arcCount = b.capacity(KIND_LIVE)
    const dots = Math.max(8, Math.floor(arcCount / 3))
    const perDot = allocate(arcCount, new Array(dots).fill(1))

    for (let d = 0; d < dots; d++) {
        const t = (d + 0.5) / dots
        const direction = slerp(from, to, t)
        const lift = GLOBE_RADIUS + 0.2 + 2.4 * Math.sin(Math.PI * t)

        for (let k = 0; k < perDot[d]; k++) {
            b.paint(direction[0] * lift, direction[1] * lift, direction[2] * lift, mixRgb(P.bright, P.signal, Math.sin(Math.PI * t)), 0.9, 1.0)
        }
    }

    b.use(KIND_GROUND).jitter(0.03)
    b.style(P.point, 0.16, 0.85)
    b.ring(0, 0, 0, GLOBE_RADIUS * 1.32, b.capacity(KIND_GROUND), true)

    return b.build({
        key: 'globe',
        anchors: {
            stockholm: latLon(STOCKHOLM.lat, STOCKHOLM.lon, GLOBE_RADIUS * 1.01),
            lisbon: latLon(LISBON.lat, LISBON.lon, GLOBE_RADIUS * 1.01)
        },
        frame: { x: 0, y: 0, z: 0, rx: 0, ry: 0, rz: 0, w: 1, h: 1 },
        camera: { pitch: 18, distance: 46, targetY: 0 },
        radius: GLOBE_RADIUS * 1.4,
        height: GLOBE_RADIUS
    })
}
